import { faArrowRotateLeft, faFile } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { Link, useParams } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ExportPDF from "./ExportPDF";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchReport,
  fetchReportCities,
  fetchReportSpeakers,
} from "../../redux/features/report/reportSlice";
import ExcelReport from "./ExcelReport";
import FilterReport from "./FilterReport";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#ff6361"];

function ReportPresentation() {
  const dispatch = useDispatch();
  const { cities, speakers, getReportLoading, selectedReport } = useSelector(
    (state) => state.report,
  );
  const reportRef = useRef(null);
  const { id } = useParams();
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    speaker: "",
    city: "",
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchReport({ id }));
      dispatch(fetchReportCities(id));
      dispatch(fetchReportSpeakers(id));
    }
  }, [dispatch, id]);

  const handleApplyFilters = () => {
    dispatch(
      fetchReport({
        id,
        speaker_name: filters.speaker || undefined,
        city: filters.city || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
      }),
    );
  };

  const handleClearFilters = () => {
    const resetFilters = {
      dateFrom: "",
      dateTo: "",
      speaker: "",
      city: "",
    };
    setFilters(resetFilters);
    dispatch(fetchReport({ id })); // Fetch original data
  };

  const hasNoData =
    !selectedReport ||
    !selectedReport.questions ||
    selectedReport.questions.length === 0;

  return (
    <div className="min-h-screen flex flex-col bg-lightPurple/5">
      <Navbar />

      <div className="max-w-[95vw] mx-auto w-full flex justify-between mt-8 px-[1vw] ">
        <Link to="/presentations">
          <button className="p-2 text-sm rounded-lg bg-purple-400 hover:bg-purple-500 text-white font-bold w-24 uppercase">
            <FontAwesomeIcon icon={faArrowRotateLeft} className="mr-1" />
            Back
          </button>
        </Link>

        <div className="flex items-center gap-4">
          <ExcelReport id={id} selectedReport={selectedReport} />
          <ExportPDF targetRef={reportRef} />
          <FilterReport
            selectedReport={selectedReport}
            setFilters={setFilters}
            filters={filters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            cities={cities}
            speakers={speakers}
          />
        </div>
      </div>

      <div className="flex-grow">
        {getReportLoading ? (
          <div className="text-center text-[2vw] font-semibold mt-10">
            Loading Report...
          </div>
        ) : hasNoData ? (
          <div className="text-center mt-20">
            <h2 className="text-3xl font-semibold text-gray-500 capitalize">
              No Data Found!
            </h2>
          </div>
        ) : (
          <div>
            <div ref={reportRef} className="text-center mb-[1vw]">
              <div className="p-[1vw] max-w-[85vw] mx-auto">
                <h2 className="text-[2vw] font-semibold mb-[2.5vw]">
                  📑 Presentation Report
                </h2>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-[1vw] ">
                  <div className="bg-gray-50 p-[1vw] shadow-sm rounded">
                    <div className="font-bold text-blue-600 text-2xl">
                      {selectedReport?.total_questions}
                    </div>
                    <div className="text-gray-500 text-lg font-medium">
                      Total Questions
                    </div>
                  </div>

                  <div className="bg-gray-50 p-[1vw] shadow-sm rounded">
                    <div className="font-bold text-green-600 text-2xl">
                      {selectedReport?.questions_answered}
                    </div>
                    <div className="text-gray-500 text-lg font-medium">
                      Questions Answered
                    </div>
                  </div>

                  <div className="bg-gray-50 p-[1vw] shadow-sm rounded">
                    <div className="font-bold text-purple-600 text-2xl">
                      {selectedReport?.total_responses}
                    </div>
                    <div className="text-gray-500 text-lg font-medium">
                      Total Responses
                    </div>
                  </div>

                  <div className="bg-gray-50 p-[1vw] shadow-sm rounded">
                    <div className="font-bold text-orange-600 text-2xl">
                      {selectedReport?.unique_attendees}
                    </div>
                    <div className="text-gray-500 text-lg font-medium">
                      Unique Attendees
                    </div>
                  </div>
                </div>

                <div className="mt-10 px-4 max-w-7xl mx-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(selectedReport?.questions || []).map((q, index) => (
                      <div
                        key={q.id}
                        className="bg-gray-50 p-4 rounded shadow-md flex justify-center"
                      >
                        <div className="w-full ">
                          <h3 className="font-semibold my-4 text-xl text-center px-[2vw]">
                            {index + 1}- {q.title}
                          </h3>

                          <div className="w-full h-[400px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={q.choices}
                                  dataKey="count"
                                  nameKey="label"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius="70%"
                                  label
                                >
                                  {q.choices.map((_, i) => (
                                    <Cell
                                      key={i}
                                      fill={COLORS[i % COLORS.length]}
                                    />
                                  ))}
                                </Pie>

                                <Tooltip />

                                <Legend
                                  layout="vertical"
                                  verticalAlign="bottom"
                                  align="center"
                                  wrapperStyle={{ fontSize: "14px" }}
                                  formatter={(value, entry) => {
                                    const count = entry.payload?.count || 0;

                                    const displayValue =
                                      value?.length > 50
                                        ? `${value.slice(0, 50)}...`
                                        : value;

                                    return (
                                      <span className="text-gray-700 font-medium">
                                        {displayValue} , Count : {count}
                                      </span>
                                    );
                                  }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                  {(selectedReport?.questions || []).map((q, index) => (
                    <div
                      key={q.id}
                      className="bg-gray-50 p-4 rounded shadow-md flex justify-center"
                    >
                      <div>
                        <h3 className="font-semibold my-4 text-xl px-[2vw]">
                          {index + 1}- {q.title}
                        </h3>

                        <PieChart width={600} height={400}>
                          <Pie
                            data={q.choices}
                            dataKey="count"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label
                          >
                            {q.choices.map((_, index) => (
                              <Cell
                                key={index}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>

                          <Tooltip />

                          <Legend
                            layout="vertical"
                            verticalAlign="bottom"
                            align="center"
                            formatter={(value, entry) => {
                              const count = entry.payload?.count || 0;
                              // Truncate the text if it's too long
                              const displayValue =
                                value?.length > 55
                                  ? `${value.slice(0, 55)}...`
                                  : value;

                              return (
                                <span className="text-gray-700 font-medium">
                                  {displayValue} , Count : {count}
                                </span>
                              );
                            }}
                          />
                        </PieChart>
                      </div>
                    </div>
                  ))}
                </div> */}
              </div>
            </div>
            <div className="max-w-[85vw] mx-auto my-6 p-[1vw]">
              <div className="relative border border-gray-300 rounded-lg">
                <div
                  className="max-h-[40rem] overflow-auto"
                  style={{
                    scrollbarWidth: "thin",
                  }}
                >
                  <table className="w-full text-left ">
                    <thead className="sticky top-0 bg-gray-50 z-20">
                      <tr>
                        <th className="p-4 font-semibold border-b border-r border-gray-300 text-lg">
                          Questions
                        </th>
                        <th className="p-4 font-semibold border-b border-r border-gray-300 text-lg">
                          Choices
                        </th>
                        <th className="p-4 font-semibold border-b border-r border-gray-300 text-center text-lg">
                          Count
                        </th>
                        <th className="p-4 font-semibold border-b border-gray-300 text-lg">
                          Presentation Details (Speaker, City, Date)
                        </th>
                      </tr>
                    </thead>

                    <tbody className="bg-white">
                      {selectedReport?.questions?.map((q, qIdx) => {
                        const totalChoices = q?.choices?.length;

                        return q?.choices?.map((choice, cIdx) => (
                          <tr
                            key={`${q.id}-${cIdx}`}
                            className="hover:bg-gray-100 transition-colors"
                          >
                            {/* Question Column:this display question (index) only once until choices of this question is finished */}
                            {cIdx === 0 ? (
                              <td
                                className="p-4 align-top border-r border-b border-gray-300 bg-gray-50/50"
                                rowSpan={totalChoices}
                              >
                                <div className="flex flex-col">
                                  <span className="text-lg font-bold uppercase text-lightPurple mb-1">
                                    Question {qIdx + 1}
                                  </span>
                                  <span className="text-lg font-medium text-gray-900 line-clamp-3">
                                    {q.title.slice(0, 50)}
                                  </span>
                                </div>
                              </td>
                            ) : null}

                            <td className="p-4 text-gray-600 border-r border-b border-gray-300 text-lg">
                              {choice.label.slice(0, 50)}
                            </td>

                            <td className="p-4 text-center border-r border-b border-gray-300">
                              <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-purple-50 text-lightPurple font-bold text-base">
                                {choice.count}
                              </span>
                            </td>

                            <td className="p-4 border-b border-gray-300">
                              <div className="flex flex-col gap-2">
                                {selectedReport?.speakers?.map(
                                  (speaker, sIdx) => (
                                    <div
                                      key={sIdx}
                                      className="flex items-center gap-2 text-base bg-white border border-gray-200 p-1.5 rounded shadow-sm"
                                    >
                                      <span className="font-semibold capitalize text-gray-800 border-r pr-2">
                                        {speaker.name}
                                      </span>
                                      <span className="text-gray-500 font-medium capitalize">
                                        {speaker.city}
                                      </span>
                                      <span className="ml-auto text-purple-400">
                                        {speaker.date || "No Date"}
                                      </span>
                                    </div>
                                  ),
                                )}
                              </div>
                            </td>
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default ReportPresentation;
